import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatApiError, formatUAH, downloadFile } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Printer, FileSpreadsheet, FileText, Lock, Calendar,
  Edit2, Trash2, Sparkles, ShieldCheck, GripVertical, CalendarClock,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { SearchableSelect, normalizeDiopter } from "@/components/SearchableSelect";
import { toast } from "sonner";

const STATUS_LABEL = { planned: "Заплановано", in_progress: "В роботі", completed: "Завершено" };
const STATUS_COLOR = {
  planned: "bg-amber-50 text-amber-700",
  in_progress: "bg-sky-50 text-sky-700",
  completed: "bg-emerald-50 text-emerald-700",
};
const OP_STATUS_COLOR = {
  scheduled: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  transferred: "bg-sky-50 text-sky-700",
};
const IOL_STATUS = [
  { value: "not_ordered", label: "Не замовлено" },
  { value: "ordered", label: "Замовлено" },
  { value: "expected", label: "Очікується" },
  { value: "received", label: "Отримано" },
  { value: "returned", label: "Повернено / Заміна" },
];
const IOL_STATUS_COLOR = {
  not_ordered: "bg-slate-100 text-slate-600",
  ordered: "bg-amber-50 text-amber-700",
  expected: "bg-sky-50 text-sky-700",
  received: "bg-emerald-50 text-emerald-700",
  returned: "bg-rose-50 text-rose-700",
};
const OP_STATUS_OPTIONS = [
  { value: "scheduled", label: "Заплановано" },
  { value: "completed", label: "Проведено" },
  { value: "transferred", label: "Перенесено" },
  { value: "cancelled", label: "Скасовано" },
];

const WEEKDAYS = ["Неділя", "Понеділок", "Вівторок", "Середа", "Четвер", "П'ятниця", "Субота"];
function weekday(s) { if (!s) return ""; return WEEKDAYS[new Date(s + "T00:00:00").getDay()]; }
function fmtDate(s) { if (!s) return ""; const [y, m, d] = s.split("-"); return `${d}.${m}.${y}`; }

const EMPTY_OP = {
  patient_id: "", time: "", date: "", operation_type: "", eye: "OD",
  iol_type: "", iol_manufacturer: "", iol_category: "", diopter: "",
  total_amount: 0, cost_amount: 0, paid_amount: 0,
  payment_status: "unpaid", is_primary: true, comment: "",
  status: "scheduled", recommendation_id: "", recommendation_item_id: "",
  surgeon_id: "", iol_status: "not_ordered", iol_sn: "",
  zero_cost_reason: "",  // Iteration 29
};

// Iteration 29: reasons for zero-cost operations
const ZERO_COST_REASONS = [
  { value: "free", label: "Безкоштовно" },
  { value: "warranty", label: "Гарантійна" },
  { value: "charity", label: "Благодійна" },
  { value: "other", label: "Інша причина" },
];

export default function OperationalDayDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [opTypes, setOpTypes] = useState([]);
  const [iols, setIols] = useState([]);
  const [iolCats, setIolCats] = useState([]);
  const [user, setUser] = useState(null);
  const [opOpen, setOpOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_OP);
  const [patientRecs, setPatientRecs] = useState([]);
  const [usedItemIds, setUsedItemIds] = useState(new Set());
  const [transferFor, setTransferFor] = useState(null);
  const [transferData, setTransferData] = useState({ new_date: "", new_time: "", reason: "" });
  const [orderedIds, setOrderedIds] = useState([]);
  const [paymentPrompt, setPaymentPrompt] = useState(null);
  // Iteration 32: filters/search for the operations table.
  const [opSearch, setOpSearch] = useState("");
  const [iolFilter, setIolFilter] = useState("all");
  const [iolStatusFilter, setIolStatusFilter] = useState("all");
  const [noteFilter, setNoteFilter] = useState("all");

  const load = async () => {
    // Iteration 31: use allSettled so a single 403 on a helper endpoint
    // (e.g. /employees when employees.read override excluded the role) does not
    // leave the whole page stuck on "Завантаження...". Individual failures
    // are surfaced as targeted toasts so admins know which permission to grant.
    const results = await Promise.allSettled([
      api.get(`/operation-days/${id}`),
      api.get("/patients"),
      api.get("/employees"),
      api.get("/settings/operation-types"),
      api.get("/settings/iol-types"),
      api.get("/settings/iol-categories"),
      api.get("/auth/me"),
    ]);
    const [d, p, e, t, i, c, u] = results;
    if (d.status === "fulfilled") {
      setDay(d.value.data);
      setOrderedIds((d.value.data.operations || []).map((o) => o.id));
    } else {
      toast.error(formatApiError(d.reason?.response?.data?.detail) || "Не вдалося завантажити операційний день");
    }
    if (p.status === "fulfilled") setPatients(p.value.data);
    else { setPatients([]); toast.error("Немає доступу до пацієнтів (patients.read)"); }
    if (e.status === "fulfilled") setEmployees(e.value.data);
    else { setEmployees([]); toast.error("Немає доступу до працівників (employees.read)"); }
    setOpTypes(t.status === "fulfilled" ? t.value.data : []);
    setIols(i.status === "fulfilled" ? i.value.data : []);
    setIolCats(c.status === "fulfilled" ? c.value.data : []);
    if (u.status === "fulfilled") setUser(u.value.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Iteration 28: scroll to operation row when navigated with #op-<id> anchor
  const location = useLocation();
  useEffect(() => {
    if (!day) return;
    const hash = location.hash || "";
    const m = hash.match(/^#op-(.+)$/);
    if (!m) return;
    const opId = m[1];
    // Wait a tick for the table to render
    const t = setTimeout(() => {
      const el = document.getElementById(`op-${opId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("op-row-highlight");
      setTimeout(() => el.classList.remove("op-row-highlight"), 2500);
    }, 250);
    return () => clearTimeout(t);
  }, [day, location.hash]);

  useEffect(() => {
    if (!form.patient_id) { setPatientRecs([]); setUsedItemIds(new Set()); return; }
    Promise.all([
      api.get(`/recommendations?patient_id=${form.patient_id}`),
      api.get(`/operations?patient_id=${form.patient_id}`),
    ]).then(([recsR, opsR]) => {
      setPatientRecs(recsR.data || []);
      // Items used by any non-cancelled operation (excluding the one being edited)
      const used = new Set();
      for (const o of (opsR.data || [])) {
        if (o.status === "cancelled") continue;
        if (editing && o.id === editing.id) continue;
        if (o.recommendation_item_id) used.add(o.recommendation_item_id);
      }
      setUsedItemIds(used);
    }).catch(() => { setPatientRecs([]); setUsedItemIds(new Set()); });
  }, [form.patient_id, editing]);

  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);
  const surgeonById = useMemo(() => {
    return Object.fromEntries((day?.surgeons || []).map((sid) => [sid, empById[sid]?.full_name || sid]));
  }, [day, empById]);

  const isDirector = user?.role === "admin" || user?.role === "director";
  const dayLocked = day?.status === "completed";
  const canReorder = !dayLocked || isDirector;

  const applyRecommendation = (recId) => {
    if (!recId || recId === "none") { setForm({ ...form, recommendation_id: "", recommendation_item_id: "" }); return; }
    const rec = patientRecs.find((r) => r.id === recId);
    if (!rec) return;
    const items = rec.items || [];
    const pt = rec.patient_type || (rec.referral_source && rec.referral_source !== "self" ? "referred" : "primary");
    // If single item, auto-select; else require explicit pick
    if (items.length === 1) {
      applyRecItem(rec, items[0]);
    } else {
      setForm({
        ...form,
        recommendation_id: rec.id,
        recommendation_item_id: "",
        is_primary: pt === "primary",
        referrer_employee_id: "",
        referral_source: rec.referral_source || "",
      });
    }
  };

  const applyRecItem = (rec, item) => {
    const pt = rec.patient_type || (rec.referral_source && rec.referral_source !== "self" ? "referred" : "primary");
    setForm({
      ...form,
      recommendation_id: rec.id,
      recommendation_item_id: item.id || "",
      operation_type: item.procedure_name || rec.operation_type || form.operation_type,
      eye: item.eye || rec.eye || form.eye,
      // Iteration 10: IOL/diopter NOT prefilled from recommendation — entered as factual op data
      total_amount: item.estimated_amount > 0 ? item.estimated_amount : form.total_amount,
      is_primary: pt === "primary",
      referrer_employee_id: "",
      referral_source: rec.referral_source || "",
    });
  };

  const selectedRec = patientRecs.find((r) => r.id === form.recommendation_id);

  const manualConfirm = async (op) => {
    if (!window.confirm("Підтвердити нарахування ЗП по цій операції без рекомендації? Дія записується в журнал.")) return;
    try {
      await api.post(`/recommendations/manual-confirm/${op.id}`);
      toast.success("Підтверджено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!day) return <div className="text-slate-500">Завантаження...</div>;

  // operations sorted by orderedIds (drag state) or as returned by API
  const opsById = Object.fromEntries((day.operations || []).map((o) => [o.id, o]));
  const orderedOps = orderedIds.map((oid) => opsById[oid]).filter(Boolean);

  const patientById = Object.fromEntries((patients || []).map((p) => [p.id, p]));
  const iolByName = Object.fromEntries((iols || []).map((i) => [i.name, i]));

  const ops = orderedOps.filter((o) => {
    if (opSearch) {
      const q = opSearch.trim().toLowerCase();
      const patient = patientById[o.patient_id];
      const iol = iolByName[o.iol_type];
      const hay = [
        patient?.full_name,
        patient?.phone,
        o.iol_type,
        iol?.abbreviation,
        iol?.manufacturer,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (iolFilter !== "all") {
      if (iolFilter === "__none__") { if (o.iol_type) return false; }
      else if (o.iol_type !== iolFilter) return false;
    }
    if (iolStatusFilter !== "all" && (o.iol_status || "not_ordered") !== iolStatusFilter) return false;
    if (noteFilter !== "all") {
      const c = (o.comment || "").toLowerCase();
      if (noteFilter === "one_eye" && !c.includes("одне око")) return false;
      if (noteFilter === "two_eyes" && !(c.includes("два ока") || c.includes("двоє очей") || c.includes("обидва") || c.includes("2 ока"))) return false;
      if (noteFilter === "with_note" && !o.comment) return false;
      if (noteFilter === "without_note" && o.comment) return false;
    }
    return true;
  });

  // Distinct IOL names used in this day (for the filter dropdown).
  const iolsInDay = Array.from(new Set(orderedOps.map((o) => o.iol_type).filter(Boolean))).sort();

  const openCreate = () => {
    const defaultSurgeon = (day.surgeons || []).length === 1 ? day.surgeons[0] : "";
    setEditing(null);
    setForm({ ...EMPTY_OP, date: day.date, operation_day_id: day.id, surgeon_id: defaultSurgeon });
    setOpOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    const iol = iols.find((i) => i.name === o.iol_type);
    setForm({ ...EMPTY_OP, ...o, iol_manufacturer: o.iol_manufacturer || iol?.manufacturer || "" });
    setOpOpen(true);
  };
  const save = async () => {
    if (!form.patient_id) { toast.error("Оберіть пацієнта"); return; }
    if ((day.surgeons || []).length > 1 && !form.surgeon_id) {
      toast.error("Оберіть хірурга з призначених на цей операційний день"); return;
    }
    if (form.iol_status === "received" && !(form.iol_sn || "").trim() && ["phaco","phaco_external","surgery"].includes(day?.branch || "phaco")) {
      toast.error("Для статусу «Отримано» потрібен SN ІОЛ"); return;
    }
    // Diopter required only for IOL-based branches; anti_vegf and laser don't need it.
    const requiresIol = ["phaco", "phaco_external", "surgery"].includes(day?.branch || "phaco");
    const diopter = normalizeDiopter(form.diopter || "");
    if (requiresIol && !diopter) {
      toast.error("Заповніть «Діоптрія» через кому (напр. 21,0). Крок 0,5"); return;
    }
    try {
      const payload = { ...form, diopter: requiresIol ? diopter : "", operation_day_id: day.id, date: day.date };
      if (editing) await api.put(`/operations/${editing.id}`, payload);
      else await api.post("/operations", payload);
      toast.success("Збережено");
      setOpOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeOp = async (o) => {
    // Iteration 29: only superadmin can delete; require reason.
    if (user?.role !== "admin") {
      toast.error("Видалення операцій дозволено лише суперадміну");
      return;
    }
    const reason = window.prompt(
      `Видалити операцію пацієнта ${o.patient_name}?\nВкажіть причину (обов'язково):`, ""
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error("Причина обов'язкова"); return; }
    try {
      await api.delete(`/operations/${o.id}`, { data: { reason: reason.trim() } });
      toast.success("Операцію видалено (soft-delete)"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTransfer = (o) => {
    setTransferFor(o);
    setTransferData({ new_date: day.date, new_time: o.time || "", reason: "" });
  };
  const submitTransfer = async () => {
    if (!transferData.new_date) { toast.error("Вкажіть нову дату"); return; }
    try {
      await api.post(`/operations/${transferFor.id}/transfer`, transferData);
      toast.success("Операцію перенесено");
      setTransferFor(null);
      // If moved to a different day, navigate
      if (transferData.new_date !== day.date) {
        // refresh — will load same day; user can navigate via the list
        await load();
      } else {
        await load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeDay = async () => {
    if (!window.confirm("Закрити операційний день? Після закриття його статус буде «Завершено».")) return;
    try { await api.post(`/operation-days/${id}/close`); toast.success("День закрито"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteDay = async () => {
    const opsCnt = day?.summary?.operations_count ?? 0;
    const msg = opsCnt > 0
      ? `Видалити операційний день ${day.date} разом із ${opsCnt} операціями?\nЦЮ ДІЮ НЕМОЖЛИВО СКАСУВАТИ.`
      : `Видалити операційний день ${day.date}?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/operation-days/${id}`, { params: { cascade: true } });
      toast.success("День видалено");
      navigate("/operational-days");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportExcel = async () => {
    const r = await api.get(`/operation-days/${id}/excel`, { responseType: "blob" });
    downloadFile(r.data, `day_${day.date}.xlsx`);
  };
  const exportPdf = async () => {
    const r = await api.get(`/operation-days/${id}/pdf`, { responseType: "blob" });
    downloadFile(r.data, `day_${day.date}.pdf`);
  };
  const printDay = () => window.print();

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const next = Array.from(orderedIds);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrderedIds(next);
    try {
      await api.post("/operations/reorder", { operation_day_id: day.id, ids: next });
      toast.success("Порядок збережено");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      await load();
    }
  };

  // Quick patch helpers used by inline editors
  // Iteration 34: when marking an operation as "completed" with outstanding debt,
  // ask for the actual final payment so the balance/debt is updated in one step.
  const handleStatusChange = (op, newStatus) => {
    if (newStatus !== "completed") { patchOp(op.id, { status: newStatus }); return; }
    const total = Number(op.total_amount || 0);
    const paid = Number(op.paid_amount || 0);
    const debt = Math.max(0, total - paid);
    if (debt <= 0) { patchOp(op.id, { status: newStatus }); return; }
    setPaymentPrompt({
      op,
      total, paid, debt,
      amount: debt,
      method: "cash",
      comment: "",
    });
  };

  const submitPayment = async () => {
    if (!paymentPrompt) return;
    const { op, amount, method, comment } = paymentPrompt;
    const amt = Number(amount || 0);
    try {
      if (amt > 0) {
        await api.post(`/operations/${op.id}/payment`, { amount: amt, method, comment });
      }
      await api.patch(`/operations/${op.id}/quick`, { status: "completed" });
      setPaymentPrompt(null);
      toast.success(amt > 0 ? `Оплату ${amt.toLocaleString('uk-UA')} ₴ додано, операцію проведено` : "Операцію проведено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchOp = async (op_id, body) => {
    try {
      await api.patch(`/operations/${op_id}/quick`, body);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const s = day.summary;
  const canSeeSF = !!day.financial_perms?.can_see_salary_fund;
  const canSeeProfit = !!day.financial_perms?.can_see_profit;

  return (
    <div data-testid="day-detail-page" className="space-y-6 print:space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/operational-days")} data-testid="back-to-days">
            <ArrowLeft className="h-4 w-4 mr-1" /> Назад
          </Button>
          <div>
            <h1 className="heading text-3xl font-bold text-[#0F2027]">
              Операційний день · {fmtDate(day.date)}
            </h1>
            <div className="text-sm text-slate-500 flex items-center flex-wrap gap-2 mt-1">
              <Calendar className="h-3.5 w-3.5" /> {weekday(day.date)}
              <Badge className={`${STATUS_COLOR[day.status]} hover:${STATUS_COLOR[day.status]}`}>{STATUS_LABEL[day.status]}</Badge>
              {day.branch_label && (
                <Badge className="bg-teal-50 text-teal-700 border border-teal-200" data-testid="day-detail-branch">{day.branch_label}</Badge>
              )}
              {(day.surgeon_names || []).length > 0 && (
                <span className="text-slate-600">· Хірурги: <span className="font-medium text-[#0F2027]">{(day.surgeon_names || []).join(", ")}</span></span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openCreate} data-testid="add-op-to-day" className="bg-[#0F2027] hover:bg-[#0F2027]/90" disabled={dayLocked && !isDirector}>
            <Plus className="h-4 w-4 mr-1" /> Додати операцію
          </Button>
          <Button variant="outline" size="sm" onClick={printDay} data-testid="print-day"><Printer className="h-4 w-4 mr-1" />Друк</Button>
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          {day.status !== "completed" && (
            <Button variant="outline" size="sm" onClick={closeDay} data-testid="close-day" className="text-red-600 border-red-200 hover:bg-red-50">
              <Lock className="h-4 w-4 mr-1" />Закрити день
            </Button>
          )}
          {(user?.role === "admin" || user?.role === "director") && (
            <Button variant="outline" size="sm" onClick={deleteDay} data-testid="delete-day"
              className="text-rose-700 border-rose-300 hover:bg-rose-50">
              <Trash2 className="h-4 w-4 mr-1" />Видалити день
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className={`grid grid-cols-2 md:grid-cols-4 ${canSeeSF || canSeeProfit ? 'lg:grid-cols-7' : 'lg:grid-cols-5'} gap-3 print:grid-cols-7`}>
        <SummaryCell label="Операцій" value={s.operations_count} />
        <SummaryCell label="Хірургів" value={(day.surgeons || []).length} />
        <SummaryCell label="Сума" value={formatUAH(s.total_amount)} tone="teal" />
        <SummaryCell label="Оплачено" value={formatUAH(s.paid_amount)} tone="green" />
        <SummaryCell label="Борг" value={formatUAH(s.debt_amount)} tone="red" />
        {canSeeSF && <SummaryCell label="Фонд ЗП" value={formatUAH(s.salary_fund)} testid="cell-salary-fund" />}
        {canSeeProfit && <SummaryCell label="Прибуток" value={formatUAH(s.estimated_profit)} tone="teal" testid="cell-profit" />}
      </div>

      {/* Operations table with DnD */}
      <Card className="gv-card print:shadow-none print:border-slate-300">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
            <div className="font-semibold text-[#0F2027]">Операції ({ops.length}{ops.length !== orderedOps.length && ` з ${orderedOps.length}`})</div>
            {dayLocked && <span className="text-xs text-rose-600 flex items-center gap-1"><Lock className="h-3 w-3" /> День закрито — редагування захищених полів дозволено лише директору</span>}
          </div>
          <div className="p-3 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap print:hidden">
            <Input
              data-testid="op-search"
              placeholder="Пошук: пацієнт, IOL, абревіатура…"
              value={opSearch}
              onChange={(e) => setOpSearch(e.target.value)}
              className="w-64"
            />
            <Select value={iolFilter} onValueChange={setIolFilter}>
              <SelectTrigger className="w-56" data-testid="op-filter-iol"><SelectValue placeholder="Кришталик" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі IOL</SelectItem>
                <SelectItem value="__none__">— без IOL —</SelectItem>
                {iolsInDay.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={iolStatusFilter} onValueChange={setIolStatusFilter}>
              <SelectTrigger className="w-44" data-testid="op-filter-iol-status"><SelectValue placeholder="Статус IOL" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі статуси IOL</SelectItem>
                {IOL_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={noteFilter} onValueChange={setNoteFilter}>
              <SelectTrigger className="w-44" data-testid="op-filter-note"><SelectValue placeholder="Примітка" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Усі примітки</SelectItem>
                <SelectItem value="one_eye">Одне око</SelectItem>
                <SelectItem value="two_eyes">Два ока</SelectItem>
                <SelectItem value="with_note">З приміткою</SelectItem>
                <SelectItem value="without_note">Без примітки</SelectItem>
              </SelectContent>
            </Select>
            {(opSearch || iolFilter !== "all" || iolStatusFilter !== "all" || noteFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setOpSearch(""); setIolFilter("all"); setIolStatusFilter("all"); setNoteFilter("all"); }} data-testid="op-filter-reset">
                Скинути
              </Button>
            )}
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="ops">
              {(provided) => (
                <div className="overflow-x-auto" ref={provided.innerRef} {...provided.droppableProps}>
                  <table className="w-full text-sm">
                    <thead className="bg-white text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                      <tr>
                        <th className="w-8 print:hidden" />
                        <th className="text-left p-2 w-16">Час</th>
                        <th className="text-left p-2">ПІБ</th>
                        <th className="text-left p-2 w-12">Вік</th>
                        <th className="text-left p-2">Хірург</th>
                        <th className="text-left p-2">ІОЛ</th>
                        <th className="text-left p-2 w-12">Око</th>
                        <th className="text-left p-2 w-20">Діоптрія</th>
                        <th className="text-right p-2">Сума</th>
                        <th className="text-right p-2">Аванс</th>
                        <th className="text-right p-2">Доплата</th>
                        <th className="text-left p-2">Статус ІОЛ</th>
                        <th className="text-left p-2">SN ІОЛ</th>
                        <th className="text-left p-2">Статус операції</th>
                        <th className="text-left p-2">Примітка</th>
                        <th className="text-right p-2 print:hidden">Дії</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ops.map((o, idx) => (
                        <Draggable key={o.id} draggableId={o.id} index={idx} isDragDisabled={!canReorder}>
                          {(prov, snap) => (
                            <tr
                              ref={prov.innerRef}
                              id={`op-${o.id}`}
                              {...prov.draggableProps}
                              className={`border-t border-slate-100 hover:bg-slate-50/50 ${snap.isDragging ? "bg-teal-50" : ""}`}
                              data-testid={`op-row-${o.id}`}
                            >
                              <td className="p-2 print:hidden" {...prov.dragHandleProps}>
                                {canReorder && <GripVertical className="h-4 w-4 text-slate-300 cursor-grab" />}
                              </td>
                              <td className="p-2 font-mono tabular-nums text-[#0F2027]">{o.time || "—"}</td>
                              <td className="p-2 font-medium text-[#0F2027]">
                                {o.patient_id ? (
                                  <Link
                                    to={`/patients/${o.patient_id}`}
                                    data-testid={`op-patient-link-${o.id}`}
                                    className="hover:text-[#0D9488] hover:underline decoration-dotted underline-offset-2 transition-colors"
                                    title="Відкрити картку пацієнта (загальна інформація + рекомендації)"
                                  >
                                    {o.patient_name}
                                  </Link>
                                ) : o.patient_name}
                              </td>
                              <td className="p-2 text-slate-700">{o.patient_age ?? "—"}</td>
                              <td className="p-2 text-slate-700">{o.surgeon_name || <span className="text-slate-400 italic">—</span>}</td>
                              <td className="p-2 text-slate-600 text-xs">{o.iol_type || "—"}</td>
                              <td className="p-2">
                                <Badge variant="outline" className={
                                  o.eye === "OD" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                  : o.eye === "OS" ? "border-rose-300 bg-rose-50 text-rose-700"
                                  : ""
                                }>{o.eye}</Badge>
                              </td>
                              <td className="p-2 text-slate-700 font-mono">{o.diopter || "—"}</td>
                              <td className="p-2 text-right font-semibold tabular-nums">{formatUAH(o.total_amount)}</td>
                              <td className="p-2 text-right tabular-nums text-emerald-700">{formatUAH(o.paid_amount)}</td>
                              <td className="p-2 text-right tabular-nums text-red-600">{o.debt > 0 ? formatUAH(o.debt) : "—"}</td>
                              <td className="p-2 print:hidden">
                                <InlineIolStatus op={o} onChange={(v) => patchOp(o.id, { iol_status: v })} />
                              </td>
                              <td className="p-2 print:hidden">
                                <InlineIolSn op={o} onSave={(sn) => patchOp(o.id, { iol_sn: sn })} />
                              </td>
                              <td className="p-2 print:hidden">
                                <InlineOpStatus op={o} onChange={(v) => handleStatusChange(o, v)} />
                              </td>
                              <td className="p-2 text-slate-600 max-w-[180px] truncate" title={o.comment}>{o.comment || "—"}</td>
                              <td className="p-2 text-right print:hidden">
                                <div className="flex gap-1 justify-end items-center">
                                  {o.status === "completed" && (
                                    o.salary_manually_confirmed ? (
                                      <Badge className="bg-sky-50 text-sky-700 hover:bg-sky-50 mr-1" title="ЗП підтверджена вручну">
                                        <ShieldCheck className="h-3 w-3 mr-1" />Підтв.
                                      </Badge>
                                    ) : !o.recommendation_id && !o.is_primary && isDirector ? (
                                      <Button size="sm" variant="outline" onClick={() => manualConfirm(o)}
                                              data-testid={`manual-confirm-${o.id}`}
                                              className="text-sky-700 border-sky-200 hover:bg-sky-50 mr-1">
                                        <ShieldCheck className="h-3.5 w-3.5 mr-1" />Підтв. ЗП
                                      </Button>
                                    ) : null
                                  )}
                                  <Button size="icon" variant="ghost" onClick={() => openTransfer(o)} data-testid={`transfer-op-${o.id}`} title="Перенести">
                                    <CalendarClock className="h-4 w-4 text-sky-600" />
                                  </Button>
                                  <Button size="icon" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-op-${o.id}`}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  {user?.role === "admin" && (
                                    <Button size="icon" variant="ghost" onClick={() => removeOp(o)} data-testid={`delete-op-${o.id}`} title="Видалити (суперадмін)">
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {ops.length === 0 && (
                        <tr><td colSpan={16} className="p-8 text-center text-slate-400">Немає операцій</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </CardContent>
      </Card>

      {/* Operation create/edit dialog */}
      <Dialog open={opOpen} onOpenChange={setOpOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Редагувати операцію" : "Нова операція"} · {fmtDate(day.date)}</DialogTitle>
            <DialogDescription>
              Бригаду (лікар / менеджер / фельдшери / відрядження) зберігаємо у рекомендації — тут оперативні поля операційного дня.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>Пацієнт <span className="text-rose-500">*</span></Label>
              <SearchableSelect
                value={form.patient_id}
                onChange={(v) => setForm({ ...form, patient_id: v, recommendation_id: "", recommendation_item_id: "" })}
                options={patients.map((p) => ({
                  value: p.id,
                  label: p.full_name,
                  hint: [p.phone, p.phone_alt].filter(Boolean).join(" · "),
                  keywords: [p.full_name, p.phone, p.phone_alt].filter(Boolean).join(" "),
                }))}
                placeholder="Оберіть пацієнта"
                empty="Пацієнтів не знайдено"
                testid="op-patient"
              />
            </div>
            <div>
              <Label>Час</Label>
              <Input type="time" data-testid="op-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>

            {form.patient_id && (
              <div className="md:col-span-3 p-3 rounded-md border border-dashed border-[#0D9488] bg-teal-50/30">
                <Label className="text-sm font-semibold text-[#0F2027] flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#0D9488]" /> Прив&apos;язка до рекомендації
                </Label>
                {patientRecs.length === 0 ? (
                  <div className="text-xs text-slate-500 mt-1">
                    У пацієнта немає рекомендацій. Можна провести операцію без рекомендації — для нарахування ЗП директор підтверджує її вручну.
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <Select value={form.recommendation_id || "none"} onValueChange={applyRecommendation}>
                      <SelectTrigger data-testid="op-rec-picker"><SelectValue placeholder="— Без прив'язки —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Без прив&apos;язки —</SelectItem>
                        {patientRecs.map((r) => {
                          const itemsCount = (r.items || []).length;
                          return (
                            <SelectItem key={r.id} value={r.id}>
                              {r.diagnosis ? `${r.diagnosis} · ` : ""}{itemsCount > 0 ? `${itemsCount} рекомендованих` : r.operation_type} · {r.status === "active" ? "Активна" : "Прострочена"}{r.closed ? " (закрита)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedRec && (selectedRec.items || []).length > 1 && (
                      <div>
                        <Label className="text-xs">Рядок рекомендації <span className="text-rose-500">*</span></Label>
                        <Select
                          value={form.recommendation_item_id || ""}
                          onValueChange={(v) => {
                            const it = selectedRec.items.find((x) => x.id === v);
                            if (it) applyRecItem(selectedRec, it);
                          }}
                        >
                          <SelectTrigger data-testid="op-rec-item-picker"><SelectValue placeholder="Оберіть рядок" /></SelectTrigger>
                          <SelectContent>
                            {selectedRec.items.map((it) => {
                              const used = usedItemIds.has(it.id);
                              return (
                                <SelectItem key={it.id} value={it.id} disabled={used}>
                                  {it.procedure_name} {it.eye && `(${it.eye})`}
                                  {it.estimated_amount ? ` · ${formatUAH(it.estimated_amount)}` : ""}
                                  {used ? " — вже використано" : ""}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="text-xs text-slate-500">
                      При виборі: процедура, око, орієнтовна сума, тип пацієнта та джерело направлення підтягуються автоматично. <strong>ІОЛ та діоптрія</strong> вводяться вручну в полях нижче (фактичні дані операції).
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Хірург <span className="text-rose-500">*</span></Label>
              <Select value={form.surgeon_id || ""} onValueChange={(v) => setForm({ ...form, surgeon_id: v })}>
                <SelectTrigger data-testid="op-surgeon"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                <SelectContent>
                  {(day.surgeons || []).map((sid) => (
                    <SelectItem key={sid} value={sid}>{surgeonById[sid] || sid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(day.surgeons || []).length === 0 && <div className="text-xs text-rose-500 mt-1">У дні не призначено хірургів. Додайте у налаштуваннях дня.</div>}
            </div>
            <div>
              <Label>Вид операції</Label>
              <Select value={form.operation_type} onValueChange={(v) => {
                const t = opTypes.find((x) => x.name === v);
                setForm({ ...form, operation_type: v, total_amount: t?.default_price ?? form.total_amount });
              }}>
                <SelectTrigger><SelectValue placeholder="Оберіть" /></SelectTrigger>
                <SelectContent>{opTypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Око</Label>
              <Select value={form.eye} onValueChange={(v) => setForm({ ...form, eye: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OD">OD (праве)</SelectItem>
                  <SelectItem value="OS">OS (ліве)</SelectItem>
                  <SelectItem value="OU">OU (обидва)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {["phaco", "phaco_external", "surgery"].includes(day?.branch || "phaco") && (
              <>
                <div>
                  <Label>Виробник ІОЛ</Label>
                  <Select value={form.iol_manufacturer || ""} onValueChange={(v) => setForm({ ...form, iol_manufacturer: v, iol_type: "" })}>
                    <SelectTrigger data-testid="op-iol-manufacturer"><SelectValue placeholder="Оберіть виробника" /></SelectTrigger>
                    <SelectContent>
                      {Array.from(new Set(iols.map((i) => i.manufacturer).filter(Boolean))).sort().map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Кришталик (ІОЛ)</Label>
                  <Select value={form.iol_type} onValueChange={(v) => {
                    const iol = iols.find((i) => i.name === v);
                    const newPrice = iol?.sale_price || iol?.price || 0;
                    setForm({
                      ...form,
                      iol_type: v,
                      // Auto-fill total_amount from IOL sale_price (overrides existing).
                      total_amount: newPrice > 0 ? newPrice : form.total_amount,
                    });
                  }} disabled={!form.iol_manufacturer}>
                    <SelectTrigger data-testid="op-iol-model">
                      <SelectValue placeholder={form.iol_manufacturer ? "Оберіть модель" : "Спочатку оберіть виробника"} />
                    </SelectTrigger>
                    <SelectContent>
                      {iols
                        .filter((i) => !form.iol_manufacturer || i.manufacturer === form.iol_manufacturer)
                        .map((i) => <SelectItem key={i.id} value={i.name}>{i.name} — {formatUAH(i.sale_price || i.price)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Діоптрія <span className="text-rose-500">*</span></Label>
                  <Input
                    data-testid="op-diopter"
                    value={form.diopter || ""}
                    placeholder="напр. 21,0 або 21,5"
                    onChange={(e) => setForm({ ...form, diopter: e.target.value })}
                    onBlur={(e) => {
                      const norm = normalizeDiopter(e.target.value);
                      if (norm === null) { toast.error("Невірний формат діоптрії. Допустимо лише крок 0,5 (напр. 21,0 / 21,5 / 22,0)"); return; }
                      setForm({ ...form, diopter: norm });
                    }}
                  />
                  <div className="text-xs text-slate-500 mt-1">Тільки через кому, крок 0,5</div>
                </div>

                <div>
                  <Label>Статус ІОЛ</Label>
                  <Select value={form.iol_status || "not_ordered"} onValueChange={(v) => setForm({ ...form, iol_status: v })}>
                    <SelectTrigger data-testid="op-iol-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{IOL_STATUS.map((s2) => <SelectItem key={s2.value} value={s2.value}>{s2.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>SN ІОЛ {form.iol_status === "received" && <span className="text-rose-500">*</span>}</Label>
                  <Input data-testid="op-iol-sn" value={form.iol_sn || ""} onChange={(e) => setForm({ ...form, iol_sn: e.target.value })} />
                </div>
              </>
            )}
            <div>
              <Label>Статус операції</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{OP_STATUS_OPTIONS.map((s2) => <SelectItem key={s2.value} value={s2.value}>{s2.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div><Label>Сума</Label><Input type="number" placeholder="0" value={form.total_amount ? form.total_amount : ""} onChange={(e) => setForm({ ...form, total_amount: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
            <div><Label>Оплачено (аванс)</Label><Input type="number" placeholder="0" value={form.paid_amount ? form.paid_amount : ""} onChange={(e) => setForm({ ...form, paid_amount: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
            <div><Label>Доплата (борг)</Label>
              <Input readOnly disabled value={formatUAH(Math.max(0, (form.total_amount || 0) - (form.paid_amount || 0)))} className="bg-slate-50" />
            </div>

            {/* Iteration 29: zero-cost reason picker (only when total_amount == 0) */}
            {(!form.total_amount || form.total_amount === 0) && (
              <div className="md:col-span-3">
                <Label>Причина безкоштовної операції <span className="text-rose-500">*</span></Label>
                <Select value={form.zero_cost_reason || ""} onValueChange={(v) => setForm({ ...form, zero_cost_reason: v })}>
                  <SelectTrigger data-testid="zero-cost-reason"><SelectValue placeholder="Оберіть причину" /></SelectTrigger>
                  <SelectContent>
                    {ZERO_COST_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1">{"Обов'язково для встановлення статусу «Проведено» при сумі 0 ₴."}</div>
              </div>
            )}

            <div className="md:col-span-3"><Label>Примітка</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="save-op-in-day" className="bg-[#0F2027]">Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer operation dialog */}
      <Dialog open={!!transferFor} onOpenChange={(v) => !v && setTransferFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Перенести операцію</DialogTitle>
            <DialogDescription>
              {transferFor?.patient_name} · {transferFor?.operation_type} ({transferFor?.eye}). Дія записується в Audit Log та timeline пацієнта.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Новий операційний день <span className="text-rose-500">*</span></Label>
              <Input type="date" data-testid="transfer-date" value={transferData.new_date} onChange={(e) => setTransferData({ ...transferData, new_date: e.target.value })} />
              <div className="text-xs text-slate-500 mt-1">Якщо дня з такою датою немає — буде створено новий (без хірургів).</div>
            </div>
            <div>
              <Label>Новий час</Label>
              <Input type="time" data-testid="transfer-time" value={transferData.new_time} onChange={(e) => setTransferData({ ...transferData, new_time: e.target.value })} />
            </div>
            <div>
              <Label>Причина перенесення</Label>
              <Input data-testid="transfer-reason" value={transferData.reason} onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })} placeholder="напр. хвороба пацієнта" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransferFor(null)}>Скасувати</Button>
            <Button onClick={submitTransfer} className="bg-[#0D9488]" data-testid="transfer-save">Перенести</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iteration 34: fact-payment prompt when marking operation as completed */}
      <Dialog open={!!paymentPrompt} onOpenChange={(v) => !v && setPaymentPrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Провести операцію — фактична доплата</DialogTitle>
          </DialogHeader>
          {paymentPrompt && (
            <div className="space-y-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Загальна сума</div>
                  <div className="font-semibold text-[#0F2027] tabular-nums">{formatUAH(paymentPrompt.total)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Вже оплачено</div>
                  <div className="font-semibold text-emerald-700 tabular-nums">{formatUAH(paymentPrompt.paid)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Залишок боргу</div>
                  <div className="font-semibold text-red-600 tabular-nums" data-testid="pmt-debt">{formatUAH(paymentPrompt.debt)}</div>
                </div>
              </div>
              <div>
                <Label>Сума доплати</Label>
                <Input
                  type="number"
                  data-testid="pmt-amount"
                  value={paymentPrompt.amount === 0 ? "" : paymentPrompt.amount}
                  onChange={(e) => setPaymentPrompt({ ...paymentPrompt, amount: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                />
                <div className="text-xs text-slate-500 mt-1">За замовчуванням — сума боргу. Введіть 0, якщо пацієнт нічого не доплатив.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Метод оплати</Label>
                  <Select value={paymentPrompt.method} onValueChange={(v) => setPaymentPrompt({ ...paymentPrompt, method: v })}>
                    <SelectTrigger data-testid="pmt-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Готівка</SelectItem>
                      <SelectItem value="card">Картка</SelectItem>
                      <SelectItem value="transfer">Переказ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Коментар</Label>
                  <Input value={paymentPrompt.comment} onChange={(e) => setPaymentPrompt({ ...paymentPrompt, comment: e.target.value })} data-testid="pmt-comment" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentPrompt(null)}>Скасувати</Button>
            <Button onClick={submitPayment} data-testid="pmt-submit" className="bg-[#0F2027]">Провести операцію</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InlineIolStatus({ op, onChange }) {
  return (
    <Select value={op.iol_status || "not_ordered"} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-40" data-testid={`iol-status-${op.id}`}>
        <SelectValue>
          <Badge className={`${IOL_STATUS_COLOR[op.iol_status || "not_ordered"]} hover:${IOL_STATUS_COLOR[op.iol_status || "not_ordered"]}`}>
            {op.iol_status_label || "Не замовлено"}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {IOL_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function InlineIolSn({ op, onSave }) {
  const [val, setVal] = React.useState(op.iol_sn || "");
  React.useEffect(() => setVal(op.iol_sn || ""), [op.iol_sn]);
  const required = op.iol_status === "received";
  return (
    <Input
      data-testid={`iol-sn-${op.id}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || "") !== (op.iol_sn || "")) onSave(val); }}
      placeholder={required ? "Обов'язково" : "—"}
      className={`h-8 w-28 text-xs font-mono ${required && !val ? "border-rose-300" : ""}`}
    />
  );
}

function InlineOpStatus({ op, onChange }) {
  return (
    <Select value={op.status} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-36" data-testid={`op-status-${op.id}`}>
        <SelectValue>
          <Badge className={`${OP_STATUS_COLOR[op.status]} hover:${OP_STATUS_COLOR[op.status]}`}>
            {op.status_label || op.status}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>{OP_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function SummaryCell({ label, value, tone = "navy", testid }) {
  const cls = {
    navy: "text-[#0F2027]",
    teal: "text-[#0D9488]",
    green: "text-emerald-700",
    red: "text-red-600",
  }[tone];
  return (
    <Card className="gv-card print:shadow-none print:border-slate-300" data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-lg font-bold tabular-nums ${cls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
